/**
 * "Open work" gadget (Build): what is left to do, in three numbers. The release gate's failing checks
 * (hooks/useQualityGate, shared with Needs attention and Ready to publish), the secrets a service declares and nobody
 * set (the app configuration, shared with Jobs and Configuration health), and the TODO / FIXME notes in the services'
 * code (vfs-service's code search, `GET /grep`, at most TODO_MAX). Each number that could not be read says so instead
 * of a 0.
 */
import React from 'react';
import PropTypes from 'prop-types';
import useQualityGate from '../../../hooks/useQualityGate';
import { failingRules, secretsToSet, todoCount } from '../../../utils/homeGadgets/build';
import GadgetCard, { TabLink } from './GadgetCard';
import { TODO_MAX, useAppConfig, useTodo } from './sources';

function Tile({ value, label, testId, bad = false }) {
  return (
    <div className="gd-tile" data-testid={testId}>
      <strong className={bad ? 'gd-bad' : ''}>{value}</strong>
      <span>{label}</span>
    </div>
  );
}
Tile.propTypes = { bad: PropTypes.bool, label: PropTypes.string, testId: PropTypes.string, value: PropTypes.node };

export default function OpenWork({ projectId, onOpenQuality = null, onOpenConfig = null }) {
  const { gate, loading: gateLoading, error: gateError } = useQualityGate(projectId);
  const config = useAppConfig(projectId);
  const todo = useTodo(projectId);
  const failing = failingRules(gate);
  const secrets = secretsToSet(config.data?.overview);
  const todos = todoCount(todo.data, TODO_MAX);
  const neverRan = Boolean(gate?.neverRan);
  const value = (loading, error, n, extra = '') => {
    if (error && n === null) { return '—'; }
    if (loading && n === null) { return '…'; }
    return `${n}${extra}`;
  };
  return (
    <GadgetCard
      links={<><TabLink onClick={onOpenQuality}>Quality</TabLink><TabLink onClick={onOpenConfig}>Configuration</TabLink></>}
      testId="open-work"
      title="Open work"
    >
      <div className="gd-tiles">
        <Tile bad={failing.length > 0} label={neverRan ? 'checks: not run yet' : 'failing checks'} testId="open-work-checks" value={value(gateLoading, gateError, gate ? failing.length : null)} />
        <Tile bad={secrets.length > 0} label="secrets to set" testId="open-work-secrets" value={value(config.loading, config.error, config.data ? secrets.length : null)} />
        <Tile label="TODO / FIXME" testId="open-work-todo" value={value(todo.loading, todo.error, todo.data ? todos.count : null, todos.capped ? '+' : '')} />
      </div>
      {failing.length > 0 && (
        <ul className="gd-rows">
          {failing.slice(0, 3).map((rule) => <li className="gd-row" key={rule.id}><span className="gd-bad">✕</span><span className="gd-grow">{rule.missing ? <strong>Missing: </strong> : null}{rule.detail || rule.label}</span></li>)}
        </ul>
      )}
      {secrets.length > 0 && <p className="gd-note">To set: {secrets.slice(0, 4).map((s) => `${s.name} (${s.service})`).join(', ')}{secrets.length > 4 ? '…' : ''}</p>}
      {gateError && !gate && <p className="gd-note">The checks could not be read just now.</p>}
      {config.error && !config.data && <p className="gd-note">The secrets could not be read just now.</p>}
      {todo.error && !todo.data && <p className="gd-note" data-testid="open-work-todo-error">The code could not be searched for TODO and FIXME just now.</p>}
      {todo.data && todos.count > 0 && <p className="gd-note">In {todos.files} {todos.files === 1 ? 'file' : 'files'} of the services.</p>}
    </GadgetCard>
  );
}

OpenWork.propTypes = { onOpenConfig: PropTypes.func, onOpenQuality: PropTypes.func, projectId: PropTypes.string.isRequired };
